import { ReportStage } from 'donor/portal/pages/FeedbackDashboard/api';
import { FINAL_BUDGET_QUESTION_ID } from './Components/Budget/utils';
import { isImpactSummaryAndDetailsValid } from './Components/ImpactIndicator/utils';
import {
  getFinalizeMarkerQuestionId,
  isReportFinalized,
  isReportInterim,
} from './Components/ReportFinalize/utils';
import { Answer } from './Components/Review/utils';
import { NavSection } from './constants';

export const INTERIM_PROGRESS_NOTES_QUESTION_ID = 120004;
export type ValidationType = 'warning' | 'error';

export type ValidationResult = {
  type: ValidationType;
  text: string;
  id: number;
};

export type ValidateFn = (
  qId: number,
  answer: Answer,
  validations: Record<number, unknown>,
  sectionAnswers: Answer[],
) => ValidationResult | undefined;

export type GetValidationKeysFn = () => Record<number, unknown>;

interface NavOptionProps {
  sections: NavSection[];
  reportAnswers: Answer[];
  isImpactModelValid: boolean;
  reportStage: ReportStage | undefined;
  sectionFilter: Record<string, string>;
  sectionIds: {
    impactSummary: number;
    impactDetails: number;
  };
  getValidationKeys: GetValidationKeysFn;
  validate: ValidateFn;
  isCapacityDevelopmentEnabled: (
    capacity: string | string[] | undefined,
  ) => boolean;
}

interface InterimSectionAnswer {
  info: Answer | undefined;
  results: Answer | undefined;
  beneficiaries: Answer | undefined;
  notes: Answer | undefined;
  budget: Answer | undefined;
}

const getInterimSectionAnswers = (
  reportAnswers: Answer[],
  isInterim: boolean | undefined,
  isImpactModelValid: boolean,
): InterimSectionAnswer => {
  const result: InterimSectionAnswer = {
    info: undefined,
    results: undefined,
    beneficiaries: undefined,
    notes: undefined,
    budget: undefined,
  };
  if (!isInterim) {
    return result;
  }
  for (const a of reportAnswers) {
    if (a.questionId === 120001) {
      result.info = a;
    }
    if (a.questionId === 120002) {
      result.results = a;
    }
    if (a.questionId === 120003) {
      result.beneficiaries = a;
    }
    if (a.questionId === INTERIM_PROGRESS_NOTES_QUESTION_ID) {
      result.notes = a;
    }
    if (a.questionId === 120005) {
      result.budget = a;
    }
  }

  return result;
};

export const applyNavOptionValidity = ({
  sections,
  reportAnswers,
  isImpactModelValid,
  reportStage = 'INITIAL',
  sectionFilter,
  sectionIds,
  getValidationKeys,
  validate,
  isCapacityDevelopmentEnabled,
}: NavOptionProps): NavSection[] => {
  const isInterim = reportStage ? isReportInterim(reportStage) : false;
  const isFinal = reportStage ? isReportFinalized(reportStage) : false;

  const interimSectionAnswers = getInterimSectionAnswers(
    reportAnswers,
    isInterim,
    isImpactModelValid,
  );

  const impactSummaryText = reportAnswers?.find(ans => ans.questionId === 40015)
    ?.text;
  const impactDetailsText = reportAnswers?.find(ans => ans.questionId === 50001)
    ?.text;

  const { isImpactDetailsValid } = isImpactSummaryAndDetailsValid(
    impactSummaryText,
    impactDetailsText,
    isCapacityDevelopmentEnabled(
      reportAnswers?.find(ans => ans.questionId === 40021)?.text,
    ),
    reportAnswers?.find(ans => ans.questionId === 40007)?.text === 'Yes',
    reportAnswers?.find(ans => ans.questionId === 40008)?.text === 'Yes',
  );

  sections.forEach(sec => {
    sec.options.forEach(opt => {
      if (isInterim && sec.id === 2) {
        if (opt.text === 'Program Overview') {
          opt.valid = Boolean(interimSectionAnswers.info);
        }
        if (opt.text === 'Interim Forecasts') {
          opt.valid =
            Boolean(interimSectionAnswers.results) && isImpactDetailsValid;
        }
        if (opt.text === 'Beneficiaries') {
          opt.valid = Boolean(interimSectionAnswers.beneficiaries);
        }
        if (opt.text === 'Progress Notes') {
          opt.valid = Boolean(interimSectionAnswers.notes);
        }
        if (opt.text === 'Budget') {
          opt.valid = Boolean(interimSectionAnswers.budget);
        }
        return;
      }
      if (opt.text === 'Review & Submit') {
        opt.valid = false;
        return;
      }
      if (opt.text === 'Impact Model Details') {
        opt.valid = isImpactModelValid;
        return;
      }
      if (opt.text === 'Final Results') {
        const resultsQuestionId = getFinalizeMarkerQuestionId(2, reportStage);
        const results = reportAnswers?.find(
          ans => ans.questionId === resultsQuestionId,
        )?.text;
        opt.valid = isImpactModelValid && Boolean(results);
        return;
      }
      if (
        sec.id === 3 &&
        (opt.id === 1 || opt.id === 2 || opt.id === 3 || opt.id === 5)
      ) {
        const finalizeQuestionId = getFinalizeMarkerQuestionId(
          opt.id,
          reportStage,
        );
        const finalizeAnswers = reportAnswers.filter(
          a =>
            a.question.section === 'finalize_report' &&
            a.question.dbId === finalizeQuestionId,
        );
        opt.valid = finalizeAnswers.length > 0;
        return;
      }

      opt.error = false;
      const sectionKey = sectionFilter[opt.text];
      opt.valid =
        reportAnswers.filter(
          a =>
            a.question.section === sectionKey &&
            !a.question.nextQuestionId &&
            !a?.enumResponse?.nextQuestionId,
        ).length > 0;

      if (
        opt.id === sectionIds.impactSummary ||
        opt.id === sectionIds.impactDetails
      ) {
        opt.valid =
          opt.id === sectionIds.impactSummary
            ? Boolean(impactSummaryText)
            : isImpactDetailsValid;
      }

      opt.warning = false;
      if (opt.valid) {
        const sectionAnswers = reportAnswers.filter(
          a => a.question.section === sectionKey,
        );
        const validations = getValidationKeys();
        sectionAnswers.forEach(ans => {
          const errorVal = validate(
            ans?.question?.dbId,
            ans,
            validations,
            sectionAnswers,
          );
          if (errorVal?.type === 'warning') {
            opt.warning = true;
          }
          if (errorVal?.type === 'error') {
            opt.error = true;
          }
        });
      }
    });
  });

  return sections;
};
